import { useState, useCallback, useMemo, memo } from 'react';
import { X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';

import { FormInput, PhoneInput, VerificationCode } from '@/components';
import { useApi } from '@/api/useApi';
import { useMutationProvider } from '@/hooks';
import { isVerified } from '@/utility';
import NotificationManager from '@/managers/notification-manager.tsx';

import { ForgetPasswordFormProps, FormType, ResetPasswordFormData, NewPasswordFormData } from './forget-password-form.types';
import { resetPasswordSchema, newPasswordSchema } from './forget-password-form.schema';
import { FORM_TYPES, FORM_TITLES } from './forget-password-form.constants';

import '@/assets/styles/components/_forget-password-form.styles.scss';
import ExceptionManager from '@/managers/exception-manager.tsx';

const FormHeader = memo(({ title, description, showCloseButton, onClose }: { title: string; description?: string; showCloseButton: boolean; onClose: () => void }) => (
	<div className="login-modal-header">
		<h2>{title}</h2>
		{description && <p>{description}</p>}
		{showCloseButton && (
			<button
				className="close-button"
				onClick={onClose}>
				<X size={20} />
			</button>
		)}
	</div>
));

FormHeader.displayName = 'FormHeader';

const ErrorMessage = memo(({ message }: { message: string }) => (
	<div className="error-message">
		<i className="icons-info-circle" />
		{message}
	</div>
));

ErrorMessage.displayName = 'ErrorMessage';

const ForgetPasswordForm = ({ onClose, onFormChange, showCloseButton = true }: ForgetPasswordFormProps) => {
	const [activeForm, setActiveForm] = useState<FormType>(() => FORM_TYPES.RESET_PASSWORD);
	const [verificationCode, setVerificationCode] = useState<string | null>(null);
	const [verificationCodeStatus, setVerificationCodeStatus] = useState<boolean | null>(null);

	const resetPasswordForm = useForm<ResetPasswordFormData>({
		resolver: yupResolver(resetPasswordSchema),
		mode: 'onChange',
		defaultValues: useMemo(
			() => ({
				phone: ''
			}),
			[]
		)
	});

	const newPasswordForm = useForm<NewPasswordFormData>({
		resolver: yupResolver(newPasswordSchema),
		mode: 'onChange',
		defaultValues: useMemo(
			() => ({
				password: '',
				confirmPassword: ''
			}),
			[]
		)
	});

	const { PasswordResetControllerApi } = useApi();

	const sendPasswordResetSms = useMutationProvider(
		useCallback((phone: string) => PasswordResetControllerApi.sendPasswordResetSms(phone), [PasswordResetControllerApi]),
		{
			onSuccess: useCallback(() => setActiveForm(FORM_TYPES.OTP), []),
			onError: (error: any) => {
				console.log('error', error);
				const customErrors = ['LE-USER106', 'LE-USER107'];
				if (customErrors.includes(error?.response?.data?.error)) {
					resetPasswordForm.setError('phone', {
						type: 'manual',
						message: 'Kullanıcı adı veya şifre hatalı'
					});
				} else {
					ExceptionManager.errorHandler(error);
				}
			}
		}
	);

	const verifyPasswordResetCode = useMutationProvider(
		useCallback((body: { phone: string; verificationCode: string }) => PasswordResetControllerApi.verifyPasswordResetCode(body.phone, body.verificationCode), [PasswordResetControllerApi])
	);

	const resetPasswordWithCode = useMutationProvider(
		useCallback(
			(body: { phone: string; verificationCode: string; newPassword: string }) =>
				PasswordResetControllerApi.resetPasswordWithCode(body.phone, body.verificationCode, { newPassword: body.newPassword }),
			[PasswordResetControllerApi]
		),
		{
			onSuccess: useCallback(() => {
				NotificationManager.notifyPasswordSuccessfullyReset();
				onFormChange('login');
			}, [onFormChange])
		}
	);

	const handleVerificationComplete = useCallback(
		async ({ phone, verificationCode }: { phone: string; verificationCode: string }) => {
			if (verifyPasswordResetCode.isLoading) return;

			const res = await verifyPasswordResetCode.mutateAsync({
				phone,
				verificationCode
			});
			const verificationStatus = isVerified(res);

			setVerificationCodeStatus(verificationStatus);

			if (verificationStatus) {
				setActiveForm(FORM_TYPES.NEW_PASSWORD);
			}
		},
		[verifyPasswordResetCode]
	);

	const resetPassword = useCallback(
		async ({ phone, verificationCode, newPassword }: { phone: string; verificationCode: string; newPassword: string }) => {
			if (resetPasswordWithCode.isLoading) return;
			await resetPasswordWithCode.mutateAsync({
				phone,
				verificationCode,
				newPassword
			});
		},
		[resetPasswordWithCode]
	);

	const handlePhoneSubmit = useCallback(
		(data: ResetPasswordFormData) => {
			sendPasswordResetSms.mutate(data.phone);
		},
		[sendPasswordResetSms]
	);

	const handleBackToLogin = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			onFormChange('login');
		},
		[onFormChange]
	);

	const ResetPasswordForm = useMemo(() => {
		const {
			handleSubmit,
			formState: { errors, isSubmitting, isValid },
			setValue,
			watch
		} = resetPasswordForm;

		const isDisabledForm = isSubmitting || !isValid || sendPasswordResetSms.isLoading;

		return (
			<form
				onSubmit={handleSubmit(handlePhoneSubmit)}
				className="login-form">
				<PhoneInput
					value={watch('phone') || ''}
					onChange={value => setValue('phone', value, { shouldValidate: true })}
				/>
				{errors.phone && <ErrorMessage message={errors.phone.message || ''} />}

				<button
					type="submit"
					className={clsx('submit-button', { disabled: isDisabledForm })}
					disabled={isDisabledForm}>
					Şifreni Sıfırla
				</button>

				<button
					className="back-to-login"
					onClick={handleBackToLogin}>
					Giriş Yapma Sayfasına Geri Dön
				</button>
			</form>
		);
	}, [resetPasswordForm, sendPasswordResetSms.isLoading, handlePhoneSubmit, handleBackToLogin]);

	const OTPForm = useMemo(
		() => (
			<div className="forget-password-form-otp">
				<VerificationCode
					disabled={verifyPasswordResetCode.isLoading}
					phone={resetPasswordForm.watch('phone') || ''}
					onNext={(code: string) => {
						setVerificationCode(code);
						handleVerificationComplete({
							phone: resetPasswordForm.watch('phone'),
							verificationCode: code
						});
					}}
					verificationCodeStatusIsOk={verificationCodeStatus as boolean}
				/>
			</div>
		),
		[verifyPasswordResetCode.isLoading, resetPasswordForm, verificationCodeStatus, handleVerificationComplete]
	);

	const NewPasswordForm = useMemo(() => {
		const {
			handleSubmit,
			formState: { errors, isSubmitting, isValid },
			register,
			watch,
			setValue
		} = newPasswordForm;

		const isDisabledForm = isSubmitting || !isValid;
		const password = watch('password');
		const confirmPassword = watch('confirmPassword');

		const handleNewPasswordSubmit = async (data: NewPasswordFormData) => {
			try {
				await resetPassword({
					phone: resetPasswordForm.watch('phone'),
					verificationCode: verificationCode!,
					newPassword: data.password
				});
			} catch (error) {
				console.error('Error:', error);
			}
		};

		return (
			<form
				onSubmit={handleSubmit(handleNewPasswordSubmit)}
				className="login-form">
				<FormInput
					label="Şifre"
					variant="password"
					{...register('password')}
					value={password || ''}
					onChange={e => setValue('password', e.target.value, { shouldValidate: true })}
					placeholder="Şifrenizi yazın"
					error={Boolean(errors.password)}
					showRequirements
				/>

				<FormInput
					label="Şifre Tekrarı"
					variant="password"
					{...register('confirmPassword')}
					value={confirmPassword || ''}
					onChange={e =>
						setValue('confirmPassword', e.target.value, {
							shouldValidate: true
						})
					}
					placeholder="Şifrenizi tekrar yazın"
					error={Boolean(errors.confirmPassword)}
				/>

				{errors.confirmPassword && confirmPassword?.length === 6 && password?.length === 6 && <ErrorMessage message={errors.confirmPassword.message || ''} />}

				<button
					type="submit"
					className={clsx('submit-button', { disabled: isDisabledForm })}
					disabled={isDisabledForm}>
					Şifreni Belirle
				</button>
			</form>
		);
	}, [newPasswordForm, resetPassword, resetPasswordForm, verificationCode]);

	const formRenders = useMemo(
		() => ({
			[FORM_TYPES.RESET_PASSWORD]: ResetPasswordForm,
			[FORM_TYPES.OTP]: OTPForm,
			[FORM_TYPES.NEW_PASSWORD]: NewPasswordForm
		}),
		[ResetPasswordForm, OTPForm, NewPasswordForm]
	);

	return (
		<>
			{FORM_TITLES[activeForm].showHeader && (
				<FormHeader
					title={FORM_TITLES[activeForm].title}
					description={FORM_TITLES[activeForm].description}
					showCloseButton={showCloseButton}
					onClose={onClose}
				/>
			)}
			{formRenders[activeForm]}
		</>
	);
};

export default memo(ForgetPasswordForm);
